/**
 * Where a SharePoint row lives, in the words a person would use: the site and the folders around the item,
 * read off the item's own `web_url`.
 *
 * It exists for the SEARCH results. Searching reaches across the whole tenant, where a repeated file name
 * is ordinary — 19 of 150 rows shared a name with another row on one live tenant — so a row showing only
 * its name is a row nobody can tell from the next one. In the tree the place is already on screen as the
 * breadcrumbs above the level, which is why nothing there asks for this.
 *
 * The URL is the only source: the service already sends it, and asking for a path would be a second round
 * trip for something every hit is carrying.
 */

/** What separates the parts on screen, and inside the accessible name of the row. */
const PART_SEPARATOR = ' / ';

/** Stands in for the folders dropped out of the middle of a deep path. */
const DROPPED_MIDDLE = '…';

/**
 * How many parts a place may show before the middle is dropped. A place is a hint beside a file name, not
 * a second breadcrumb trail, so it stays short enough to read in one glance: the first part says which
 * corner of the tenant the file is in, the last two say which shelf, and the folders between them are the
 * least identifying thing about a file — two rows that differ only there are rare, while a place long
 * enough to push the name off the row is certain.
 */
const MAX_PARTS = 4;

/** What a personal OneDrive is called, since the URL names its owner instead of a place. */
const ONEDRIVE_LABEL = 'OneDrive';

/**
 * The place to show beside a row, or `''` when the URL does not name one — a blank `web_url`, something
 * that is not a URL at all, or a file straight in the host root. A row without a place simply shows none.
 */
export function sharePointPlace(webUrl: string): string {
  const segments = pathSegments(webUrl);
  if (segments.length === 0) return '';
  return shorten(placeParts(segments)).join(PART_SEPARATOR);
}

/** The URL's path as decoded segments, empty for anything that will not parse as a URL. */
function pathSegments(webUrl: string): string[] {
  let path: string;
  try {
    path = new URL(webUrl).pathname;
  } catch {
    return [];
  }
  return path
    .split('/')
    .filter((segment) => segment !== '')
    .map(decodeSegment);
}

/** One segment as a person reads it. A malformed escape is shown as it came rather than thrown over. */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * The parts of the place: everything but the item itself, with SharePoint's own routing prefix resolved.
 * `/sites/` and `/teams/` are routing and not a folder, so they drop and the site name leads. A personal
 * OneDrive (`/personal/<owner>/…`) has no site: the segment after `personal` is the owner's login with the
 * dots and the `@` mangled into underscores, and passing that off as a site would be both unreadable and
 * untrue, so the whole prefix becomes the one word that is true of every such URL.
 */
function placeParts(segments: string[]): string[] {
  const aroundTheItem = segments.slice(0, -1);
  const [prefix, ...rest] = aroundTheItem;
  if (prefix === 'personal') return [ONEDRIVE_LABEL, ...rest.slice(1)];
  if (prefix === 'sites' || prefix === 'teams') return rest;
  return aroundTheItem;
}

/** A deep path with its middle dropped, for the reason {@link MAX_PARTS} gives. */
function shorten(parts: string[]): string[] {
  if (parts.length <= MAX_PARTS) return parts;
  return [...parts.slice(0, 1), DROPPED_MIDDLE, ...parts.slice(-2)];
}
